// src/components/auth/ForgotPasswordDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Chip,
  Box,
  LinearProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Visibility,
  VisibilityOff,
  LockReset,
  MailOutline,
  Key as KeyIcon,
  CheckCircle,
  ErrorOutline,
  Replay,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  requestPasswordReset,
  verifyResetOtp,
  resetPassword,
  setEmail,
  setStep,
  clearResetState,
} from "../../store/Slices/passwordResetSlice";

const GOLD = "#F0B90B";

export default function ForgotPasswordDialog({
  open,
  onClose,
  defaultEmail = "",
}) {
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const { step, email, tempResetId, loading, error, successMsg } = useSelector(
    (s) => s.passwordReset
  );

  const [localEmail, setLocalEmail] = useState(defaultEmail || email || "");
  const [otp, setOtp] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0); // ثواني
  const canResend = resendCooldown === 0 && !loading;

  // قوة كلمة المرور (بسيطة وسريعة)
  const pwdStrength = useMemo(() => {
    let score = 0;
    if (newPwd.length >= 8) score += 1;
    if (/[A-Z]/.test(newPwd)) score += 1;
    if (/[a-z]/.test(newPwd)) score += 1;
    if (/\d/.test(newPwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(newPwd)) score += 1;
    return Math.min(score, 5);
  }, [newPwd]);

  const pwdStrengthLabel = ["Weak", "Weak", "Fair", "Good", "Strong", "Strong"][
    pwdStrength
  ];
  const pwdStrengthValue = (pwdStrength / 5) * 100;

  useEffect(() => {
    if (open) {
      if (defaultEmail) dispatch(setEmail(defaultEmail));
      dispatch(setStep(0));
    } else {
      dispatch(clearResetState());
      setLocalEmail(defaultEmail || "");
      setOtp("");
      setNewPwd("");
      setShowPwd(false);
      setResendCooldown(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultEmail]);

  // بدء عدّاد إعادة الإرسال عند دخول خطوة OTP
  useEffect(() => {
    if (step === 1 && resendCooldown === 0) {
      setResendCooldown(60); // 60 ثانية مثل أغلب المنصات
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // تشغيل العدّاد
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const handleRequest = async () => {
    if (!localEmail) return;
    dispatch(setEmail(localEmail));
    const action = await dispatch(requestPasswordReset(localEmail));
    if (action.meta?.requestStatus === "fulfilled") {
      setResendCooldown(60);
    }
  };

  const handleVerify = () => {
    if (!tempResetId || !otp) return;
    dispatch(verifyResetOtp({ tempResetId, otp }));
  };

  const handleReset = () => {
    if (!tempResetId || !newPwd) return;
    if (newPwd.length < 8) return;
    dispatch(resetPassword({ tempResetId, newPassword: newPwd }));
  };

  const handleResend = async () => {
    if (!email || !canResend) return;
    const action = await dispatch(requestPasswordReset(email));
    if (action.meta?.requestStatus === "fulfilled") {
      setResendCooldown(60);
    }
  };

  const closeAll = () => {
    dispatch(clearResetState());
    onClose?.();
  };

  // شريط الخطوات (Chips) لعرض المرحلة الحالية
  const StepsHeader = (
    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
      <Chip
        label="Email"
        variant={step === 0 ? "filled" : "outlined"}
        icon={<MailOutline />}
        sx={{
          bgcolor: step === 0 ? GOLD : "transparent",
          color: step === 0 ? "#1E2A3C" : "inherit",
          fontWeight: 600,
        }}
      />
      <Chip
        label="OTP"
        variant={step === 1 ? "filled" : "outlined"}
        icon={<KeyIcon />}
        sx={{
          bgcolor: step === 1 ? GOLD : "transparent",
          color: step === 1 ? "#1E2A3C" : "inherit",
          fontWeight: 600,
        }}
      />
      <Chip
        label="New Password"
        variant={step === 2 ? "filled" : "outlined"}
        icon={<LockReset />}
        sx={{
          bgcolor: step === 2 ? GOLD : "transparent",
          color: step === 2 ? "#1E2A3C" : "inherit",
          fontWeight: 600,
        }}
      />
      {step === 3 && (
        <Chip
          label="Done"
          color="success"
          variant="filled"
          icon={<CheckCircle />}
          sx={{ fontWeight: 600 }}
        />
      )}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={closeAll}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: theme.palette.mode === "dark" ? "#0F1419" : "#111315",
          color: "#fff",
          border: `1px solid ${
            theme.palette.mode === "dark" ? "#222" : "#222"
          }`,
          boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backdropFilter: "blur(6px)" }, // Blur احترافي
        },
      }}
    >
      {/* عنوان مع تدرّج و أيقونة */}
      <Box
        sx={{
          px: 3,
          py: 2,
          background: `linear-gradient(135deg, #0B0E11 0%, #1A1F24 100%)`,
          borderBottom: `1px solid rgba(255,255,255,0.06)`,
        }}
      >
        <DialogTitle
          sx={{
            px: 0,
            py: 0,
            m: 0,
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            color: "#fff",
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(240,185,11,0.12)",
              border: `1px solid rgba(240,185,11,0.35)`,
            }}
          >
            <LockReset sx={{ color: GOLD }} />
          </Box>
          <Box>
            <Typography
              variant={downSm ? "subtitle1" : "h6"}
              sx={{ fontWeight: 800 }}
            >
              Forgot Password
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              Secure verification to reset your password
            </Typography>
          </Box>
        </DialogTitle>

        {/* شريط تقدم أثناء التحميل */}
        {loading && (
          <LinearProgress
            sx={{
              mt: 1.5,
              height: 3,
              bgcolor: "rgba(255,255,255,0.08)",
              "& .MuiLinearProgress-bar": { bgcolor: GOLD },
              borderRadius: 2,
            }}
          />
        )}
      </Box>

      <DialogContent
        sx={{
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.00) 100%)"
              : "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.00) 100%)",
          pt: 2.5,
          pb: 1.5,
        }}
      >
        <Stack spacing={2}>
          {/* رأس الخطوات */}
          {StepsHeader}

          {/* رسائل الحالة */}
          {error && (
            <Alert
              severity="error"
              icon={<ErrorOutline />}
              sx={{
                bgcolor: "rgba(211,47,47,0.08)",
                color: "#ffb4b4",
                border: "1px solid rgba(211,47,47,0.25)",
              }}
            >
              {error}
            </Alert>
          )}
          {successMsg && (
            <Alert
              severity="success"
              icon={<CheckCircle />}
              sx={{
                bgcolor: "rgba(46,125,50,0.12)",
                color: "#caffd1",
                border: "1px solid rgba(46,125,50,0.35)",
              }}
            >
              {successMsg}
            </Alert>
          )}

          {/* الخطوة 0: إدخال الإيميل */}
          {step === 0 && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Enter your email to receive a one-time verification code.
              </Typography>
              <TextField
                label="Email"
                type="email"
                value={localEmail}
                onChange={(e) => setLocalEmail(e.target.value)}
                fullWidth
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutline sx={{ color: "rgba(255,255,255,0.7)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "rgba(255,255,255,0.03)",
                    color: "#fff",
                  },
                }}
              />
            </Stack>
          )}

          {/* الخطوة 1: إدخال OTP */}
          {step === 1 && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                We sent an OTP to <b>{email}</b>. Enter it below.
              </Typography>
              <TextField
                label="OTP Code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                inputProps={{
                  maxLength: 6,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                fullWidth
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <KeyIcon sx={{ color: "rgba(255,255,255,0.7)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    letterSpacing: 6,
                    bgcolor: "rgba(255,255,255,0.03)",
                    color: "#fff",
                  },
                }}
              />

              <Stack direction="row" alignItems="center" spacing={1}>
                <Button
                  size="small"
                  startIcon={<Replay />}
                  onClick={handleResend}
                  disabled={!canResend}
                  sx={{
                    textTransform: "none",
                    color: canResend ? GOLD : "rgba(255,255,255,0.4)",
                    "&:hover": { color: canResend ? "#d7a90a" : undefined },
                  }}
                >
                  Resend code
                </Button>
                <Typography variant="caption" sx={{ opacity: 0.65 }}>
                  {canResend ? " " : `You can resend in ${resendCooldown}s`}
                </Typography>
              </Stack>
            </Stack>
          )}

          {/* الخطوة 2: كلمة المرور الجديدة */}
          {step === 2 && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Create a new password. Use at least 8 characters and mix of
                letters, numbers, and symbols.
              </Typography>
              <TextField
                label="New Password"
                type={showPwd ? "text" : "password"}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                fullWidth
                autoFocus
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPwd((v) => !v)}
                        edge="end"
                        aria-label="toggle password"
                        sx={{ color: "rgba(255,255,255,0.8)" }}
                      >
                        {showPwd ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "rgba(255,255,255,0.03)",
                    color: "#fff",
                  },
                }}
              />

              {/* مؤشر قوة كلمة المرور */}
              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    Password strength
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {pwdStrengthLabel}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={pwdStrengthValue}
                  sx={{
                    height: 6,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.08)",
                    "& .MuiLinearProgress-bar": {
                      bgcolor:
                        pwdStrength < 3
                          ? "#ff6b6b"
                          : pwdStrength < 4
                          ? "#f0c14b"
                          : "#22c55e",
                    },
                  }}
                />
              </Box>
            </Stack>
          )}

          {/* الخطوة 3: نجاح */}
          {step === 3 && (
            <Alert
              severity="success"
              icon={<CheckCircle />}
              sx={{
                bgcolor: "rgba(46,125,50,0.12)",
                color: "#caffd1",
                border: "1px solid rgba(46,125,50,0.35)",
                mt: 0.5,
              }}
            >
              Password changed successfully. You can log in with your new
              password now.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.00) 100%)"
              : "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.00) 100%)",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          gap: 1,
        }}
      >
        {step < 3 ? (
          <>
            <Button
              onClick={closeAll}
              disabled={loading}
              variant="outlined"
              sx={{
                textTransform: "none",
                borderColor: "rgba(255,255,255,0.2)",
                color: "rgba(255,255,255,0.85)",
                "&:hover": { borderColor: "rgba(255,255,255,0.35)" },
              }}
            >
              Cancel
            </Button>

            {step === 0 && (
              <Button
                onClick={handleRequest}
                variant="contained"
                disabled={loading || !localEmail}
                sx={{
                  textTransform: "none",
                  bgcolor: GOLD,
                  color: "#1E2A3C",
                  "&:hover": { bgcolor: "#d7a90a" },
                }}
              >
                {loading ? "Sending..." : "Send Code"}
              </Button>
            )}

            {step === 1 && (
              <Button
                onClick={handleVerify}
                variant="contained"
                disabled={loading || !otp || otp.length < 4}
                sx={{
                  textTransform: "none",
                  bgcolor: GOLD,
                  color: "#1E2A3C",
                  "&:hover": { bgcolor: "#d7a90a" },
                }}
              >
                {loading ? "Verifying..." : "Verify Code"}
              </Button>
            )}

            {step === 2 && (
              <Button
                onClick={handleReset}
                variant="contained"
                disabled={loading || !newPwd || newPwd.length < 8}
                sx={{
                  textTransform: "none",
                  bgcolor: GOLD,
                  color: "#1E2A3C",
                  "&:hover": { bgcolor: "#d7a90a" },
                }}
              >
                {loading ? "Saving..." : "Change Password"}
              </Button>
            )}
          </>
        ) : (
          <Button
            onClick={closeAll}
            variant="contained"
            sx={{
              textTransform: "none",
              bgcolor: GOLD,
              color: "#1E2A3C",
              "&:hover": { bgcolor: "#d7a90a" },
            }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
